import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import Cleave from "cleave.js/react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";

//Services
import {
  COMPCONTACT_QUERY_KEY,
  companycontactService,
} from "../../../../../Services/companycontactService";

interface UpdateComtactFormProps {
  toggle?: () => void;
  id?: number;
}

const UpdateContactForm: React.FC<UpdateComtactFormProps> = ({
  toggle,
  id,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    id: id,
    name: "",
    position: "",
    email_address: "",
    mobile1: "",
    mobile2: "",
    telno1: "",
    telno2: "",
    status: 0,
  });

  const { data: contactData, isLoading: isFetching } = useQuery({
    queryKey: [COMPCONTACT_QUERY_KEY, id],
    queryFn: () => companycontactService.getContactById(id as number),
  });

  useEffect(() => {
    if (contactData) {
      setFormData({
        id: contactData.id || id,
        name: contactData.name || "",
        position: contactData.position || "",
        email_address: contactData.email_address || "",
        mobile1: contactData.mobile1 || "",
        mobile2: contactData.mobile2 || "",
        telno1: contactData.telno1 || "",
        telno2: contactData.telno2 || "",
        status: contactData.status || 0,
      });
    }
  }, [contactData]);

  const updateContactMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      companycontactService.updateContact(id as number, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: COMPCONTACT_QUERY_KEY });
      toast.success("Contact has been updated successfully");
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error("Failed to update contact.");
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleSelectedStatus = (status: number) => {
    setFormData((prev) => ({ ...prev, status: status }));
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateContactMutation.mutate(formData);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Name<b className="text-danger">*</b>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter Contact Person Name"
              maxLength={150}
              minLength={3}
              required
              disabled={isFetching}
              value={formData.name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Job Title / Position<b className="text-danger">*</b>
            </Label>
            <Input
              id="position"
              name="position"
              type="text"
              placeholder="Enter Job Title / Position"
              maxLength={150}
              minLength={3}
              required
              disabled={isFetching}
              value={formData.position}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Email Address<b className="text-danger">*</b>
            </Label>
            <Input
              id="email_address"
              name="email_address"
              type="email"
              placeholder="Enter Email Address"
              maxLength={150}
              minLength={3}
              required
              disabled={isFetching}
              value={formData.email_address}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="6">
            <Label>
              Mobile No.<b className="text-danger">*</b>
            </Label>
            <Cleave
              className="form-control"
              options={{
                delimiter: "-",
                blocks: [3, 4, 3],
                uppercase: true,
              }}
              id="mobile1"
              name="mobile1"
              type="text"
              placeholder="Enter Mobile No."
              maxLength={150}
              minLength={5}
              disabled={isFetching}
              value={formData.mobile1}
              onChange={handleInputChange}
              required
            />
          </Col>

          <Col md="6">
            <Label>Alt Mobile No.</Label>
            <Cleave
              className="form-control"
              options={{
                delimiter: "-",
                blocks: [3, 4, 3],
                uppercase: true,
              }}
              id="mobile2"
              name="mobile2"
              type="text"
              placeholder="Enter Alternative Mobile No."
              maxLength={150}
              minLength={5}
              disabled={isFetching}
              value={formData.mobile2}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="6">
            <Label>Tel No.</Label>
            <Input
              id="telno1"
              name="telno1"
              type="text"
              placeholder="Enter Tel No."
              maxLength={150}
              minLength={3}
              disabled={isFetching}
              value={formData.telno1}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="6">
            <Label>Alt Tel No.</Label>
            <Input
              id="telno2"
              name="telno2"
              type="text"
              placeholder="Enter Alternative Tel No."
              maxLength={150}
              minLength={3}
              disabled={isFetching}
              value={formData.telno2}
              onChange={handleInputChange}
            />
          </Col>
          <Col md="12">
            <Label>
              Status<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.status}
              id="status"
              name="status"
              type="select"
              disabled={isFetching}
              onChange={(e) => handleSelectedStatus(Number(e.target.value))}
            >
              <option disabled value={-1}>
                Select Status
              </option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </Col>
          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={updateContactMutation.isPending || isFetching}
            >
              {updateContactMutation.isPending
                ? "Updating Contacts..."
                : "Update"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateContactForm;
